import React from 'react';
import axios from 'axios';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import { Toaster as HotToaster } from 'react-hot-toast';
import ProtectedRoute from './components/ProtectedRoute';
import CustomerProtectedRoute from './components/CustomerProtectedRoute';
import SessionExpiredModal from './components/SessionExpiredModal';
import { installAdminAuthInterceptor } from './api/adminAuthInterceptor';
import { installCustomerAuthInterceptor } from './api/customerAuthInterceptor';
import './styles/smashzone-mobile.css';
import './styles/admin-contrast.css';
import './App.css';

// Send the httpOnly access_token/refresh_token (customer) and
// admin_access_token/admin_refresh_token (admin) cookies with every
// request — auth now lives in httpOnly cookies, not JS-readable storage,
// so the browser must attach them automatically (Aug 2026 security hardening).
axios.defaults.withCredentials = true;

// Install global 401 -> refresh interceptors once at module load.
// Admin is installed first so it gets first look at admin calls; the
// customer interceptor only acts on calls carrying the customer token.
installAdminAuthInterceptor();
installCustomerAuthInterceptor();

// Public Pages
import LandingPage from './pages/LandingPage';
import TermsPage from './pages/TermsPage';
import AuthCallback from './pages/customer/AuthCallback';
import AuthPage from './pages/customer/AuthPage';
import VerifyEmail from './pages/customer/VerifyEmail';
import ForgotPassword from './pages/customer/ForgotPassword';
import ResetPassword from './pages/customer/ResetPassword';
import AccountDeactivated from './pages/customer/AccountDeactivated';

// Customer Pages
import HomePage from './pages/customer/HomePage';
import BookingFlow from './pages/customer/BookingFlow';
import BookingsList from './pages/customer/BookingsList';
import BookingDetails from './pages/customer/BookingDetails';
import BookingSubmitted from './pages/customer/BookingSubmitted';
import PaymentPage from './pages/customer/PaymentPage';
import PaymentResult from './pages/customer/PaymentResult';
import PaymentSuccessRedirect from './pages/customer/PaymentSuccessRedirect';
import BookingConfirmed from './pages/customer/BookingConfirmed';
import ProfilePage from './pages/customer/ProfilePage';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminForgotPassword from './pages/admin/AdminForgotPassword';
import AdminLayout from './pages/admin/AdminLayout';
import CustomerLayout from './pages/customer/CustomerLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import PendingRequests from './pages/admin/PendingRequests';
import BookingsManagement from './pages/admin/BookingsManagement';
import BookingSeries from './pages/admin/BookingSeries';
import SeriesDetail from './pages/admin/SeriesDetail';
import ManualBooking from './pages/admin/ManualBooking';
import WaitlistManagement from './pages/admin/WaitlistManagement';
import ReleaseBooking from './pages/admin/ReleaseBooking';
import CustomerManagement from './pages/admin/CustomerManagement';
import StaffManagement from './pages/admin/StaffManagement';
import PricingManagement from './pages/admin/PricingManagement';
import LocationsManagement from './pages/admin/LocationsManagement';
import Analytics from './pages/admin/Analytics';
import SystemSettings from './pages/admin/SystemSettings';
import SecuritySettings from './pages/admin/SecuritySettings';
import IntegrationsManagement from './pages/admin/IntegrationsManagement';
import DbTools from './pages/admin/DbTools';
import SettingsHolidays from './pages/admin/SettingsHolidays';


// AppRouter detects the Emergent OAuth callback hash synchronously during
// render (not in a useEffect) to prevent race conditions.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();

  // If URL hash contains session_id, render the OAuth callback handler
  // BEFORE any normal routes — the session_id must be consumed first.
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Customer-facing layout (public + post-login) */}
      <Route element={<CustomerLayout />}>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/account-deactivated" element={<AccountDeactivated />} />

        {/* Customer Routes */}
        <Route path="/home" element={<CustomerProtectedRoute><HomePage /></CustomerProtectedRoute>} />
        <Route path="/book" element={<CustomerProtectedRoute redirectTo="/auth"><BookingFlow /></CustomerProtectedRoute>} />
        <Route path="/bookings" element={<CustomerProtectedRoute><BookingsList /></CustomerProtectedRoute>} />
        <Route path="/booking/:bookingId" element={<BookingDetails />} />
        <Route path="/booking-submitted/:bookingId" element={<BookingSubmitted />} />
        <Route path="/payment/:bookingId" element={<PaymentPage />} />
        <Route path="/payment/success" element={<PaymentSuccessRedirect />} />
        <Route path="/payment-result" element={<PaymentResult />} />
        <Route path="/booking-confirmed/:bookingId" element={<BookingConfirmed />} />
        <Route path="/profile" element={<CustomerProtectedRoute><ProfilePage /></CustomerProtectedRoute>} />
      </Route>

      {/* Admin Routes with RBAC Protection */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="pending" element={<PendingRequests />} />
        <Route path="bookings" element={<BookingsManagement />} />
        <Route path="bookings/series" element={<BookingSeries />} />
        <Route path="bookings/series/:id" element={<SeriesDetail />} />
        <Route path="manual-booking" element={<ManualBooking />} />
        <Route path="waitlist" element={<WaitlistManagement />} />
        <Route path="release-booking" element={
          <ProtectedRoute resource="release_paid_booking">
            <ReleaseBooking />
          </ProtectedRoute>
        } />
        <Route path="customers" element={<CustomerManagement />} />
        <Route path="staff" element={
          <ProtectedRoute resource="staff">
            <StaffManagement />
          </ProtectedRoute>
        } />
        <Route path="pricing" element={
          <ProtectedRoute resource="pricing">
            <PricingManagement />
          </ProtectedRoute>
        } />
        <Route path="settings/holidays" element={
          <ProtectedRoute resource="pricing">
            <SettingsHolidays />
          </ProtectedRoute>
        } />
        <Route path="locations" element={
          <ProtectedRoute resource="locations">
            <LocationsManagement />
          </ProtectedRoute>
        } />
        <Route path="analytics" element={
          <ProtectedRoute resource="analytics">
            <Analytics />
          </ProtectedRoute>
        } />
        <Route path="settings" element={
          <ProtectedRoute resource="settings">
            <SystemSettings />
          </ProtectedRoute>
        } />
        <Route path="integrations" element={
          <ProtectedRoute resource="integrations">
            <IntegrationsManagement />
          </ProtectedRoute>
        } />
        <Route path="security" element={
          <ProtectedRoute resource="security">
            <SecuritySettings />
          </ProtectedRoute>
        } />
        <Route path="db-tools" element={
          <ProtectedRoute resource="db_tools">
            <DbTools />
          </ProtectedRoute>
        } />
      </Route>

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <AppRouter />
          <Toaster position="top-center" richColors />
          <HotToaster position="top-right" />
          {/* Global soft-relogin overlay */}
          <SessionExpiredModal />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
